import { useState, useCallback, useEffect } from "react";
import { useSnackbar } from "notistack";
import { SitePhotoDto, UpdateSitePhotoRequest } from "../../../../../../../dtos/sitePhoto";
import { AjaxResult } from "../../../../../../../enums/ajaxResult";
import { useApi } from "../../../../../../useApi";

interface UsePhotoGalleryProps {
    siteId: number;
    onRefresh?: () => void;
}

export const usePhotoGallery = ({ siteId, onRefresh }: UsePhotoGalleryProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const { sitePhotoApi } = useApi();

    const [photos, setPhotos] = useState<SitePhotoDto[]>([]);
    const [photoUrls, setPhotoUrls] = useState<Record<number, string>>({});
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<string>("all");

    const loadPhotos = useCallback(async () => {
        setLoading(true);
        try {
            const result = await sitePhotoApi.listSitePhotos(siteId, selectedCategory === "all" ? undefined : selectedCategory);
            
            if (result.result === AjaxResult.Success && result.data) {
                setPhotos(result.data);
            } else {
                enqueueSnackbar("Failed to load photos", { variant: "error" });
            }
        } catch (error) {
            console.error("Error loading photos:", error);
            enqueueSnackbar("Failed to load photos", { variant: "error" });
        } finally {
            setLoading(false);
        }
    }, [siteId, selectedCategory, sitePhotoApi, enqueueSnackbar]);

    useEffect(() => {
        loadPhotos();
    }, [loadPhotos]);

    useEffect(() => {
        let isCancelled = false;
        const loadBlobs = async () => {
            const entries: Array<[number, string]> = [];
            for (const p of photos) {
                if (photoUrls[p.id]) continue;
                try {
                    const blob = await sitePhotoApi.downloadPhoto(siteId, p.id);
                    if (isCancelled) return;
                    const url = URL.createObjectURL(blob);
                    entries.push([p.id, url]);
                } catch (e) {
                    console.error("Failed to fetch photo blob", e);
                }
            }
            if (entries.length) {
                setPhotoUrls(prev => {
                    const updated = { ...prev };
                    entries.forEach(([id, url]) => (updated[id] = url));
                    return updated;
                });
            }
        };
        if (photos.length) {
            loadBlobs();
        }

        return () => {
            isCancelled = true;
        };
    }, [photos, siteId, sitePhotoApi, photoUrls]);

    useEffect(() => {
        return () => {
            Object.values(photoUrls).forEach((url) => URL.revokeObjectURL(url));
        };
    }, [photoUrls]);

    const handleCategoryChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setSelectedCategory(event.target.value as string);
    };

    const updatePhotoMetadata = async (photoId: number, updateData: UpdateSitePhotoRequest) => {
        try {
            const result = await sitePhotoApi.updatePhotoMetadata(siteId, photoId, updateData);

            if (result.result === AjaxResult.Success) {
                enqueueSnackbar("Photo updated successfully", { variant: "success" });
                loadPhotos();
                onRefresh && onRefresh();
                return true;
            } else {
                enqueueSnackbar("Failed to update photo", { variant: "error" });
                return false;
            }
        } catch (error) {
            console.error("Error updating photo:", error);
            enqueueSnackbar("Failed to update photo", { variant: "error" });
            return false;
        }
    };

    const deletePhoto = async (photoId: number, fileName: string) => {
        if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
            return false;
        }

        try {
            const result = await sitePhotoApi.deletePhoto(siteId, photoId);

            if (result.result === AjaxResult.Success) {
                enqueueSnackbar("Photo deleted successfully", { variant: "success" });
                loadPhotos();
                onRefresh && onRefresh();
                return true;
            } else {
                enqueueSnackbar("Failed to delete photo", { variant: "error" });
                return false;
            }
        } catch (error) {
            console.error("Error deleting photo:", error);
            enqueueSnackbar("Failed to delete photo", { variant: "error" });
            return false;
        }
    };

    const downloadPhoto = async (photoId: number, fileName: string) => {
        try {
            const blob = await sitePhotoApi.downloadPhoto(siteId, photoId);
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            link.click();
            URL.revokeObjectURL(url);
            return true;
        } catch (error) {
            console.error("Download error:", error);
            enqueueSnackbar("Failed to download photo", { variant: "error" });
            return false;
        }
    };

    return {
        photos,
        photoUrls,
        loading,
        selectedCategory,
        handleCategoryChange,
        updatePhotoMetadata,
        deletePhoto,
        downloadPhoto,
    };
}; 